import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import {
  api,
  getErrorMessage,
  prospectFilterKey,
  type Prospect,
  type ProspectCount,
  type ProspectFilters,
} from "../../api/client.ts";
import { keys } from "../../api/queryKeys.ts";
import type { UrlState, useUrlPaging } from "../../hooks/useUrlState.ts";
import type { QueueDecision } from "../../pages/prospects/queues.ts";
import { useLiveList } from "../../realtime/useLiveList.ts";

/** The rows ticked for a bulk action, on the page shown. */
export interface ProspectSelection {
  selected: Set<string>;
  toggle: (id: string) => void;
  toggleAll: () => void;
  clear: () => void;
  allSelected: boolean;
  someSelected: boolean;
}

interface Options {
  filters: ProspectFilters;
  url: UrlState;
  paging: ReturnType<typeof useUrlPaging>;
  /** Whether a decision takes the row out of what the view shows. */
  leaves: (decision: QueueDecision) => boolean;
  /** Rows shown after the page's (the worklist's snoozed ones), stepped through too. */
  extraRows?: (lastPage: boolean) => Prospect[];
}

const prospectKey = (p: Prospect) => p.id;

/**
 * A page of the tenant's prospects under `filters`, and the drawer over it:
 * the open row lives in `?prospect=` (a person opened from it beside it in
 * `?contact=`, so both link together), a linked one off this page is located
 * on the server and its page shown, J/K step through the rows, and a decision
 * moves on (dropping the row at once when it leaves the view). The rows are
 * pushed live; on a later page, with a drawer open or rows ticked they hold
 * still (`useLiveList`) and `live` feeds the page's `LiveUpdatesPill`.
 */
export function useProspectListView({ filters, url, paging, leaves, extraRows }: Options) {
  const queryClient = useQueryClient();
  const { searchParams, updateParams } = url;
  const prospectId = searchParams.get("prospect");
  const contactId = prospectId ? searchParams.get("contact") : null;
  const filterKey = prospectFilterKey(filters);
  const enabled = !!filters.tenant_id;

  // The last prospect opened stays highlighted after its drawer closes.
  const [lastOpenedId, setLastOpenedId] = useState(prospectId);
  if (prospectId && prospectId !== lastOpenedId) setLastOpenedId(prospectId);
  const highlightedId = prospectId ?? lastOpenedId;

  // Rows ticked for a bulk action; a new page or filter starts over.
  const [selected, setSelected] = useState<Set<string>>(new Set());
  useEffect(() => setSelected(new Set()), [filterKey, paging.offset]);

  const { data: latestRows, isLoading } = useQuery({
    queryKey: keys.prospect.list(filterKey, paging.offset),
    queryFn: () => api.getProspects({ ...filters, limit: paging.rowsPerPage, offset: paging.offset }),
    enabled,
    meta: { live: true },
  });
  const { data: count } = useQuery({
    queryKey: keys.prospect.count(filterKey),
    queryFn: () => api.countProspects(filters),
    enabled,
    meta: { live: true },
  });
  const live = useLiveList({
    rows: latestRows,
    getId: prospectKey,
    frozen: paging.page > 0 || !!prospectId || selected.size > 0,
    resetKey: `${filterKey}:${paging.offset}:${paging.rowsPerPage}`,
  });
  const { rows, removedIds } = live;
  const total = count?.total ?? 0;
  const lastPage = paging.offset + paging.rowsPerPage >= total;

  // A linked prospect that isn't on this page: ask the server where it sits
  // under the current filters, so newer arrivals pushing it down don't matter.
  const openOnPage = prospectId ? rows?.find((p) => p.id === prospectId) : undefined;
  const { data: located, error: locateError } = useQuery({
    queryKey: keys.prospect.locate(prospectId ?? "", filterKey),
    queryFn: () => api.locateProspect(prospectId!, filters),
    enabled: !!prospectId && !!rows && !openOnPage,
    retry: false,
    meta: { suppressErrorToast: true },
  });
  useEffect(() => {
    if (!located || located.prospect.id !== prospectId || located.index === null) return;
    const target = Math.floor(located.index / paging.rowsPerPage);
    if (target !== paging.page) updateParams({ page: target ? String(target) : null }, true);
  }, [located, prospectId, paging.page, paging.rowsPerPage, updateParams]);
  useEffect(() => {
    if (!locateError) return;
    toast.error(getErrorMessage(locateError));
    updateParams({ prospect: null, contact: null }, true);
  }, [locateError, updateParams]);

  // The rows the drawer steps through, in list order; dimmed (removed) ones are skipped.
  const stepRows = [...(rows ?? []), ...(extraRows?.(lastPage) ?? [])];
  const stepFrom = (index: number, direction: 1 | -1): Prospect | undefined => {
    for (let i = index + direction; i >= 0 && i < stepRows.length; i += direction) {
      if (!removedIds.has(stepRows[i].id)) return stepRows[i];
    }
    return undefined;
  };
  const onStep = (direction: 1 | -1) => {
    const index = stepRows.findIndex((p) => p.id === prospectId);
    const target = index >= 0 ? stepFrom(index, direction) : undefined;
    if (target) updateParams({ prospect: target.id, contact: null }, true);
  };

  // A decision from the drawer: the row goes at once (the refetch after the
  // request puts it back if it still belongs, or if the request failed), and
  // the drawer moves on to the next prospect; at the end of the list, the one
  // before it; with none left, it closes.
  const onReviewed = (decision: QueueDecision) => {
    const id = prospectId;
    if (!id) return;
    const index = stepRows.findIndex((p) => p.id === id);
    const gone = leaves(decision);
    const next = index < 0 ? undefined : (stepFrom(index, 1) ?? (gone ? stepFrom(index, -1) : undefined));
    if (gone && index >= 0) {
      live.drop(id);
      const listKey = keys.prospect.list(filterKey, paging.offset);
      void queryClient.cancelQueries({ queryKey: listKey });
      queryClient.setQueryData<Prospect[]>(listKey, (prev) => prev?.filter((p) => p.id !== id));
      queryClient.setQueryData<ProspectCount>(keys.prospect.count(filterKey), (prev) =>
        prev ? { ...prev, total: Math.max(0, prev.total - 1) } : prev,
      );
    }
    updateParams({ prospect: next?.id ?? null, contact: null }, true);
  };

  // Bring a linked or jumped-to row into view once.
  const scrolledFor = useRef<string | null>(null);
  const scrollToRow = useCallback(
    (el: HTMLTableRowElement | null) => {
      if (!el || !prospectId || scrolledFor.current === prospectId) return;
      scrolledFor.current = prospectId;
      el.scrollIntoView({ block: "center" });
    },
    [prospectId],
  );
  const open = (p: Prospect) => {
    scrolledFor.current = p.id;
    updateParams({ prospect: p.id });
  };
  const close = () => updateParams({ prospect: null, contact: null });
  // Opening a person is a step Back undoes; swapping one for another is not.
  const openContact = (id: string) => updateParams({ contact: id }, !!contactId);
  const closeContact = () => updateParams({ contact: null });

  const pageIds = rows?.flatMap((p) => (removedIds.has(p.id) ? [] : [p.id])) ?? [];
  const allSelected = pageIds.length > 0 && pageIds.every((id) => selected.has(id));
  const selection: ProspectSelection = {
    selected,
    toggle: (id) =>
      setSelected((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      }),
    toggleAll: () => setSelected(allSelected ? new Set() : new Set(pageIds)),
    clear: () => setSelected(new Set()),
    allSelected,
    someSelected: pageIds.some((id) => selected.has(id)),
  };
  const selectedProspects = stepRows.filter((p) => selected.has(p.id));

  return {
    rows,
    removedIds,
    /** For the page's `LiveUpdatesPill`. */
    live: { pendingNew: live.pendingNew, pendingRemoved: live.pendingRemoved, onApply: live.apply },
    /** Take a row out at once after the user's own removal (no dimmed leftover). */
    dropRow: live.drop,
    isLoading,
    count,
    total,
    lastPage,
    filterKey,
    prospectId,
    contactId,
    highlightedId,
    open,
    close,
    openContact,
    closeContact,
    onStep,
    onReviewed,
    scrollToRow,
    selection,
    selectedProspects,
  };
}
